import { useContext, useEffect, useState } from 'react'
import { fetchReceiptProviso } from '@/apis/aipass'
import { AccountContext } from '@/contexts/account'

type ProvisoOption = {
  value: string
  label: string
}

type UseReceiptProvisoReturn = {
  provisoOptions: ProvisoOption[]
  defaultProvision: string
  isLoading: boolean
  cacheProvision: (value: string) => void
}

const provisoStorageKey = (hotelId: string) => `receiptProvisoValue-${hotelId}`

const DEFAULT_PROVISO = '宿泊代として'

export const useReceiptProviso = (): UseReceiptProvisoReturn => {
  const { account } = useContext<any>(AccountContext)
  const [provisoOptions, setProvisoOptions] = useState<ProvisoOption[]>([])
  const [defaultProvision, setDefaultProvision] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!account?.hotel?.id) return
    const storageKey = provisoStorageKey(account.hotel.id)
    setIsLoading(true)
    fetchReceiptProviso()
      .then(res => {
        const options = res?.receiptProviso || []

        const localStorageValue = localStorage.getItem(storageKey)

        // localStorageの値が選択肢に存在すれば選択済みに、存在しなければデフォルトで存在する但し書きを選択済みにする
        const defaultValue =
          localStorageValue && options.some(item => item.proviso === localStorageValue)
            ? localStorageValue
            : options.find(item => item.isDefault)?.proviso || DEFAULT_PROVISO

        setDefaultProvision(defaultValue)

        // defaultValueを先頭に持ってくる
        const sortedOptions = [
          ...options.filter(item => item.proviso === defaultValue),
          ...options.filter(item => item.proviso !== defaultValue),
        ]
        setProvisoOptions(
          sortedOptions.map(item => ({
            value: item.proviso,
            label: item.proviso,
          })),
        )
      })
      .catch(() => {
        // 取得に失敗した場合「宿泊代として」を設定する
        setProvisoOptions([{ value: DEFAULT_PROVISO, label: DEFAULT_PROVISO }])
        setDefaultProvision(DEFAULT_PROVISO)
      })
      .finally(() => setIsLoading(false))
  }, [account?.hotel?.id])

  // ダウンロード確定時に呼び出し、次回の初期値として選択済みの但し書きを保存する
  const cacheProvision = (value: string) => {
    if (!account?.hotel?.id) return
    localStorage.setItem(provisoStorageKey(account.hotel.id), value)
  }

  return { provisoOptions, defaultProvision, isLoading, cacheProvision }
}
